import type * as ExpoFileSystem from "expo-file-system/legacy";
import { Platform } from "react-native";

import { UPLOAD_TIMEOUT_MS, UploadTimeoutError, withUploadTimeout } from "@/lib/upload-timeout";

export { UPLOAD_TIMEOUT_MS, UploadTimeoutError, withUploadTimeout };

const IS_WEB = Platform.OS === "web";

// Lazy require so vitest (node env) and the web bundle can import this module
// without booting the native FileSystem bridge. Mirrors the pattern in
// hooks/useQuestionRecorder.ts.
// eslint-disable-next-line @typescript-eslint/no-require-imports
const FileSystem = IS_WEB ? null : (require("expo-file-system/legacy") as typeof ExpoFileSystem);

/**
 * Byte size of a local file, for pre-upload size checks and for pinning
 * ContentLength into the presigned PUT.
 *
 * Uses the same lazy legacy-require as `uploadFromUri` so this module stays
 * importable from web/vitest. Returns 0 when the size can't be determined —
 * callers treat that as "unknown", not "empty".
 */
export async function getLocalFileSize(uri: string): Promise<number> {
    if (IS_WEB || !FileSystem) {
        try {
            const res = await fetch(uri);
            const blob = await res.blob();
            return blob.size;
        } catch {
            return 0;
        }
    }
    const info = await FileSystem.getInfoAsync(uri);
    return info.exists && !info.isDirectory ? info.size : 0;
}

/**
 * True for expo's `UnavailabilityError`, which it raises when a native method is
 * missing from the installed binary. Identified by its `ERR_UNAVAILABLE` code
 * (a `CodedError`), with a message check as a belt-and-braces fallback.
 */
function isNativeMethodUnavailable(err: unknown): boolean {
    if (typeof err !== "object" || err === null) {
        return false;
    }
    const code = (err as { code?: unknown }).code;
    if (code === "ERR_UNAVAILABLE") {
        return true;
    }
    const message = (err as { message?: unknown }).message;
    return typeof message === "string" && message.includes("is not available on");
}

export interface UploadFromUriParams {
    uri: string;
    uploadUrl: string;
    contentType: string;
    /** Per-file wait ceiling. Defaults to `UPLOAD_TIMEOUT_MS`. */
    timeoutMs?: number;
    /**
     * Byte-level progress for THIS file, native only (web `fetch` cannot report
     * upload progress). Optional: callers that don't pass it keep the previous
     * behaviour exactly, which is what keeps the onboarding upload path
     * untouched. `fraction` is 0..1, or null when the total size is unknown.
     */
    onProgress?: (fraction: number | null) => void;
}

/**
 * PUT a local file at `uri` to S3 (or any signed `uploadUrl`).
 *
 * Native path uses `FileSystem.uploadAsync`, which streams from disk without
 * reading the file into JS memory. The previous implementation did
 * `fetch(uri).then((r) => r.blob())` — that path is broken on Android 11+,
 * which rejects `fetch(file://)` with status 0 → `TypeError: Network request
 * failed`. That was the root cause of TASKHEALTH-MOBILE-Z (onboarding upload
 * crash on OnePlus 8 Pro). Streaming via FileSystem also keeps the heap
 * flat for large PDFs.
 *
 * Web keeps the fetch+blob path because expo-file-system is a no-op there and
 * the picker returns `blob:` / `http(s):` URIs that fetch handles fine.
 */
export async function uploadFromUri({
    uri,
    uploadUrl,
    contentType,
    timeoutMs = UPLOAD_TIMEOUT_MS,
    onProgress
}: UploadFromUriParams): Promise<void> {
    if (IS_WEB || !FileSystem) {
        const readRes = await fetch(uri);
        if (!readRes.ok) {
            throw new Error(`Failed to read local file (${readRes.status})`);
        }
        const blob = await readRes.blob();
        // Web can genuinely abort the request, so use a real signal rather than
        // just racing a timer. An abort surfaces as UploadTimeoutError so both
        // platforms fail the same way for the caller.
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), timeoutMs);
        let putRes: Response;
        try {
            putRes = await fetch(uploadUrl, {
                method: "PUT",
                body: blob,
                headers: { "Content-Type": contentType },
                signal: controller.signal
            });
        } catch (err) {
            if (controller.signal.aborted) {
                throw new UploadTimeoutError(timeoutMs);
            }
            throw err;
        } finally {
            clearTimeout(timer);
        }
        if (!putRes.ok) {
            throw new Error(`Upload failed: ${putRes.status}`);
        }
        return;
    }

    const nativeOptions = {
        httpMethod: "PUT" as const,
        uploadType: FileSystem.FileSystemUploadType.BINARY_CONTENT,
        headers: { "Content-Type": contentType }
    };

    // createUploadTask rather than uploadAsync: it reports bytes as they go (so
    // the RN sees a bar that keeps moving on a slow photo instead of a counter
    // that looks frozen) AND it exposes cancelAsync, which lets the timeout
    // actually tear a stalled transfer down instead of only abandoning the wait.
    //
    // OTA SAFETY: it reaches those two abilities through native methods
    // (`uploadTaskStartAsync`, `networkTaskCancelAsync`) that plain `uploadAsync`
    // never called. An over-the-air update ships JS onto an ALREADY-INSTALLED
    // binary, so on a build whose expo-file-system predates them expo throws
    // ERR_UNAVAILABLE. That happens before a single byte is sent, so falling back
    // to the previous call is safe and cannot double-upload. Without this the OTA
    // would fail every medication upload on those installs.
    if (typeof FileSystem.createUploadTask === "function") {
        const task = FileSystem.createUploadTask(
            uploadUrl,
            uri,
            nativeOptions,
            onProgress
                ? ({ totalBytesSent, totalBytesExpectedToSend }) => {
                      onProgress(
                          totalBytesExpectedToSend > 0
                              ? Math.min(1, totalBytesSent / totalBytesExpectedToSend)
                              : null
                      );
                  }
                : undefined
        );
        try {
            const res = await withUploadTimeout(task.uploadAsync(), timeoutMs, () => {
                void task.cancelAsync();
            });
            // cancelAsync (or a cancelled task) resolves undefined/null rather
            // than rejecting, so treat a missing result as a failure, not success.
            if (!res) {
                throw new UploadTimeoutError(timeoutMs);
            }
            if (res.status < 200 || res.status >= 300) {
                throw new Error(`Upload failed: ${res.status}`);
            }
            return;
        } catch (err) {
            // Only an unavailable native method falls through. A timeout or a
            // real S3 failure must surface, never be silently retried.
            if (!isNativeMethodUnavailable(err)) {
                throw err;
            }
        }
    }

    // Legacy path: no byte progress and no true cancellation, so the sheet keeps
    // its indeterminate spinner. The timeout still applies, so the batch can
    // still fail loudly rather than hang.
    const res = await withUploadTimeout(
        FileSystem.uploadAsync(uploadUrl, uri, nativeOptions),
        timeoutMs
    );
    if (res.status < 200 || res.status >= 300) {
        throw new Error(`Upload failed: ${res.status}`);
    }
}
